// Listing rules + small app-only helpers for the collab listings UI (docs/listings.md, sections 4 and 6).
//
// The validation half of this file is a MIRROR of
// amplify/backend/function/gcListings/src/validateListing.js (CommonJS, Lambda). CRA cannot import
// outside src/, so the rule bodies are copied verbatim; only the export syntax differs. Both copies
// run the same vectors (src/ui-components/listingValidation.cases.json). Change a rule -> change both.

export const LISTING_TYPES = ["brand", "influencer"];
export const FEE_TYPES = ["deal", "post", "day", "week", "month", "percent"];
export const FEE_LABELS = { deal: "per deal", post: "per post", day: "per day", week: "per week", month: "per month", percent: "affiliate share" };
export const ENGAGEMENT_TYPES = ["post", "like", "share", "comment"];
export const VISIBILITIES = ["public", "private"];
export const LIMITS = { title: [3, 80], description: 5000, url: 500, links: 5, slug: 60, percent: 100, fixed: 1000000, feeNote: 120, perUser: 20, contacts: 6, contactLine: 120, contactLineMin: 3, images: 10, videos: 5 };
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const EDITABLE_FIELDS = ["title", "listingType", "description", "images", "videos", "links", "feeType", "feeAmount", "feeNote", "engagementTypes", "visibility", "showOnLanding", "contacts"];
export const YOUTUBE_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtube-nocookie.com", "www.youtube-nocookie.com", "youtu.be"];
export const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
// Media (docs/listing-media.md §3.1). An images[] entry is a LEAF: opaque, server-minted, never a key.
export const MEDIA_MIN_BYTES = 1024;
export const MEDIA_MAX_BYTES = 3000000;
export const MEDIA_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"]; // NEVER image/svg+xml (§8.1)
export const MEDIA_EXT_BY_TYPE = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };
export const MEDIA_LEAF_RE = /^[0-9a-f]{32}\.(?:jpg|png|webp)$/;
export const MEDIA_PATH_PREFIX = "/collabs/media";
export const MEDIA_HARD_CAP = 30; // objects the mint endpoint allows under one slug
export const VIDEO_PROVIDERS = ["youtube", "tiktok"];
export const TIKTOK_HOSTS = ["tiktok.com", "www.tiktok.com", "m.tiktok.com"];
export const TIKTOK_SHORT_HOSTS = ["vm.tiktok.com", "vt.tiktok.com"];
export const TIKTOK_ID_RE = /^[0-9]{6,32}$/; // Snowflake-style; 19 digits today. DO NOT hard-code 19.

export function slugify(title) {
  const base = String(title || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, LIMITS.slug)
    .replace(/^-+|-+$/g, "");
  return base || "listing";
}

// RFC 3986 excludes these from a URI; the WHATWG parser does not reject all of them, and `"` in
// particular survives in a hostname (`new URL('https://e.com"x').hostname` keeps the quote), so a
// quote-bearing host could be STORED in links[]. Refusing them at the validator means no downstream
// layer has to be perfect; it also removes the `\`-reads-as-`/` divergence, because callers emit the
// raw string rather than the parser's normalised href. Same class as renderListing.js (step 5) — one
// definition of "forbidden in a URL", not two. `'` and `&` are sub-delims and stay allowed; both are
// escaped on emission. This only ever DECLINES: nothing legal by RFC 3986 is lost.
const URL_FORBIDDEN_RE = /[\u0000-\u0020\u007f-\u009f"<>`\\^{}|]/;

export function isHttpsUrl(s) {
  try {
    if (URL_FORBIDDEN_RE.test(s)) return false;
    const u = new URL(s);
    return u.protocol === "https:" && u.hostname.includes(".");
  } catch (e) {
    return false;
  }
}

export function youtubeId(url) {
  let u;
  try { u = new URL(String(url || "")); } catch (e) { return null; }
  if (!YOUTUBE_HOSTS.includes(u.hostname)) return null;
  let id;
  if (u.hostname === "youtu.be") {
    id = u.pathname.split("/")[1];
  } else {
    const m = u.pathname.match(/^\/(?:shorts|embed)\/([^/]+)/);
    id = m ? m[1] : u.searchParams.get("v");
  }
  return id && YOUTUBE_ID_RE.test(id) ? id : null;
}

/** true for the three short forms that carry ZERO id information. Rejected, never resolved (§7.4). */
export function isTiktokShortLink(s) {
  let u;
  try { u = new URL(String(s == null ? "" : s).trim()); } catch (e) { return false; }
  const host = u.hostname.toLowerCase();
  if (TIKTOK_SHORT_HOSTS.includes(host)) return true;
  return TIKTOK_HOSTS.includes(host) && u.pathname.split("/").filter(Boolean)[0] === "t";
}

/** Same shape as youtubeId(): pure, no I/O, returns an id or null. Query strings are ignored. */
export function tiktokId(url) {
  let u;
  try { u = new URL(String(url == null ? "" : url).trim()); } catch (e) { return null; }
  if (u.protocol !== "https:") return null;
  const host = u.hostname.toLowerCase();
  if (TIKTOK_SHORT_HOSTS.includes(host) || !TIKTOK_HOSTS.includes(host)) return null;
  const parts = u.pathname.split("/").filter(Boolean);
  let id = null;
  if (parts[0] === "t") id = null; // tiktok.com/t/<token> is a short link
  else if (parts[0] && parts[0].charAt(0) === "@" && (parts[1] === "video" || parts[1] === "photo")) id = parts[2];
  else if (parts[0] === "player" && parts[1] === "v1") id = parts[2];
  else if (parts[0] === "embed") id = parts[1] === "v2" ? parts[2] : parts[1];
  else if (host === "m.tiktok.com" && parts[0] === "v" && parts[1]) id = parts[1].replace(/\.html$/, "");
  return id && TIKTOK_ID_RE.test(id) ? id : null;
}

/** Accepts a pasted URL string OR an already-normalised {provider,id}. Returns {provider,id}|null. */
export function videoRef(x) {
  if (x && typeof x === "object" && !Array.isArray(x)) {
    const id = String(x.id == null ? "" : x.id).trim();
    if (x.provider === "youtube" && YOUTUBE_ID_RE.test(id)) return { provider: "youtube", id };
    if (x.provider === "tiktok" && TIKTOK_ID_RE.test(id)) return { provider: "tiktok", id };
    return null;
  }
  const s = String(x == null ? "" : x).trim();
  if (!s) return null;
  const yt = youtubeId(s);
  if (yt) return { provider: "youtube", id: yt };
  const tt = tiktokId(s);
  return tt ? { provider: "tiktok", id: tt } : null;
}

export const LISTING_PATH_PREFIX = "/collabs"; // D1: the one copy of the public URL prefix (rename = BL-6)
export const PUBLIC_SITE = "https://1stinfluencer.com";
export function publicListingUrl(slug) { return `${PUBLIC_SITE}${LISTING_PATH_PREFIX}/${slug}`; }

/** An images[] entry is a LEAF, never a key and never a URL (docs/listing-media.md §3.2). The slug
 *  comes from row.slug, never from the author, so a cross-listing media reference is unrepresentable. */
export function mediaPath(slug, leaf) { return MEDIA_PATH_PREFIX + "/" + slug + "/" + leaf; }
export function mediaUrl(slug, leaf) { return PUBLIC_SITE + mediaPath(slug, leaf); }

// The ONE reachability predicate. Used by findPublicBySlug (the read path) and by the S3 publisher
// (the write path) so they cannot drift. showOnLanding is deliberately NOT part of it: an unlisted
// listing IS reachable by URL, it is just noindex'd and absent from the index/sitemap.
export function isPubliclyReachable(l) { return Boolean(l) && l.visibility === "public" && !l.archived; }

export const NO_RATE_LABEL = "Rate on request";

/** "$500 per month" | "12% affiliate share" | "Rate on request" when the price is absent or unusable. */
export function feeLabel(l) {
  const type = l && l.feeType;
  const raw = l == null ? null : l.feeAmount;
  const n = typeof raw === "string" ? Number(raw.trim()) : Number(raw);
  if (!FEE_TYPES.includes(type) || raw === "" || raw == null || !Number.isFinite(n) || n < 0) return NO_RATE_LABEL;
  const amount = String(Math.round(n * 100) / 100);
  return type === "percent" ? `${amount}% ${FEE_LABELS.percent}` : `$${amount} ${FEE_LABELS[type]}`;
}

export function validateListing(input) {
  const src = input && typeof input === "object" ? input : {};
  const errors = {};
  const value = {};

  value.title = String(src.title || "").trim();
  if (value.title.length < LIMITS.title[0] || value.title.length > LIMITS.title[1]) errors.title = "Title must be 3–80 characters";

  value.listingType = src.listingType;
  if (!LISTING_TYPES.includes(value.listingType)) errors.listingType = "Listing type must be brand or influencer";

  value.description = String(src.description || "").trim();
  if (!value.description) errors.description = "Description is required";
  else if (value.description.length > LIMITS.description) errors.description = "Description must be at most 5000 characters";

  // images: leaves only. Always emitted (like links/contacts) so a cleared list is never "kept".
  const rawImages = Array.isArray(src.images) ? src.images : [];
  value.images = rawImages
    .map((s) => String(s == null ? "" : s).trim())
    .filter((s, i, arr) => s && arr.indexOf(s) === i);
  if (value.images.length > LIMITS.images) errors.images = "At most 10 photos are allowed";
  else if (value.images.some((s) => !MEDIA_LEAF_RE.test(s))) errors.images = "Photo reference is not valid";

  // videos: normalised, NEVER echoed — same caller contract as contacts (docs/listings.md §6).
  // Message order is load-bearing: cap, then the specific short-link message, then the generic one.
  const rawVideos = Array.isArray(src.videos) ? src.videos : [];
  const refs = rawVideos.map(videoRef);
  value.videos = refs.filter((r, i) => r && refs.findIndex((o) => o && o.provider === r.provider && o.id === r.id) === i);
  if (rawVideos.length > LIMITS.videos) errors.videos = "At most 5 videos are allowed";
  else if (rawVideos.some((v) => typeof v === "string" && isTiktokShortLink(v)))
    errors.videos = "Paste the full TikTok link — open the video and copy the address bar (tiktok.com/@name/video/…). Short vm.tiktok.com links can't be used.";
  else if (refs.some((r, i) => !r && String(rawVideos[i] == null ? "" : rawVideos[i]).trim()))
    errors.videos = "Each video must be a YouTube or TikTok link";

  const rawLinks = Array.isArray(src.links) ? src.links : [];
  value.links = rawLinks.map((l) => String(l || "").trim()).filter((l, i, arr) => l && arr.indexOf(l) === i);
  if (value.links.length > LIMITS.links) errors.links = "At most 5 links are allowed";
  else if (value.links.some((l) => l.length > LIMITS.url || !isHttpsUrl(l))) errors.links = "Each link must be an https URL of at most 500 characters";

  // Price is optional (§1.2): both parts absent = "Rate on request". A half-filled price is blocked.
  const feeType = typeof src.feeType === "string" ? src.feeType.trim() : src.feeType;
  const rawAmount = typeof src.feeAmount === "string" ? src.feeAmount.trim() : src.feeAmount;
  const hasFeeType = feeType !== undefined && feeType !== null && feeType !== "";
  const hasFeeAmount = rawAmount !== undefined && rawAmount !== null && rawAmount !== "";
  if (hasFeeType) {
    value.feeType = feeType;
    if (!FEE_TYPES.includes(feeType)) errors.feeType = "Fee type must be one of " + FEE_TYPES.join(", ");
    if (!hasFeeAmount) errors.feeAmount = "Fee amount is required when a fee type is set";
  } else if (hasFeeAmount) {
    errors.feeType = "Fee type is required when a fee amount is set";
  }
  if (hasFeeAmount) {
    const fee = Number(rawAmount);
    if (Number.isFinite(fee)) value.feeAmount = Math.round(fee * 100) / 100;
    if (!Number.isFinite(fee) || fee < 0) errors.feeAmount = "Fee amount must be a number of 0 or more";
    else if (value.feeType === "percent") { if (value.feeAmount > LIMITS.percent) errors.feeAmount = "Percent fee must be at most 100"; }
    else if (value.feeAmount > LIMITS.fixed) errors.feeAmount = "Fee amount must be at most 1,000,000";
  }

  const feeNote = String(src.feeNote || "").trim();
  if (feeNote) {
    value.feeNote = feeNote;
    if (feeNote.length > LIMITS.feeNote) errors.feeNote = "Fee note must be at most 120 characters";
  }

  const rawEngagement = Array.isArray(src.engagementTypes) ? src.engagementTypes : [];
  value.engagementTypes = rawEngagement.filter((e, i, arr) => ENGAGEMENT_TYPES.includes(e) && arr.indexOf(e) === i);
  if (!value.engagementTypes.length) errors.engagementTypes = "Select at least one engagement type";

  value.visibility = src.visibility;
  if (!VISIBILITIES.includes(value.visibility)) errors.visibility = "Visibility must be public or private";

  value.showOnLanding = Boolean(src.showOnLanding);

  // Free-form contact lines (§1.1): the list IS the public contact block, so there is nothing to opt
  // into — a missing or garbled value yields [] and publishes nothing, which still fails closed.
  // One-liners: every whitespace run (including a pasted \r\n) collapses to a single space.
  const rawContacts = Array.isArray(src.contacts) ? src.contacts : [];
  value.contacts = rawContacts
    .map((c) => String(c || "").replace(/\s+/g, " ").trim())
    .filter((c, i, arr) => c && arr.indexOf(c) === i);
  if (value.contacts.length > LIMITS.contacts) errors.contacts = "At most 6 contact lines are allowed";
  else if (value.contacts.some((c) => c.length < LIMITS.contactLineMin || c.length > LIMITS.contactLine)) errors.contacts = "Each contact line must be 3 to 120 characters";

  return { errors, value };
}

// ---- app-only helpers (not mirrored in the Lambda): emptyListing, toFormValues, statusLabel ----

export function emptyListing() {
  return { title: "", listingType: "brand", description: "", images: [], videos: [], links: [],
           feeType: "", feeAmount: "", feeNote: "", engagementTypes: ["post"], visibility: "public",
           showOnLanding: true, contacts: [] };
}

/** Server row -> form state: every emptyListing key present, arrays default [], scalars stringified, booleans kept. */
export function toFormValues(listing) {
  const l = listing && typeof listing === "object" ? listing : {};
  const base = emptyListing();
  const out = {};
  for (const k of Object.keys(base)) {
    const v = l[k];
    if (Array.isArray(base[k])) out[k] = Array.isArray(v) ? v.slice() : [];
    else if (typeof base[k] === "boolean") out[k] = v == null ? base[k] : Boolean(v);
    else out[k] = v == null ? base[k] : String(v);
  }
  return out;
}

export function statusLabel(l) {
  if (!l || l.archived) return "Archived";
  if (l.visibility !== "public") return "Private";
  return l.showOnLanding ? "Public" : "Unlisted";
}
